import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useStateCode } from "../../contexts/StateCodesContext";
import Modal from "../../components/UI/Modal";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { Description } from "@headlessui/react";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";
import StateCodeForm from "../../components/Form/StateCodeForm";

const StateCodeMasters = React.memo(() => {
  const { stateCodes, addStateCode, updateStateCode, deleteStateCode, isLoading } = useStateCode();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStateCode, setEditingStateCode] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Separate component for actions column to avoid hooks rule violation
  const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleDeleteApproverModule }) => {
    const buttonRef = useRef(null);
    
    return (
      <div className="flex items-center justify-center relative">
        <button
          ref={buttonRef}
          onClick={() =>
            setOpenMenu(openMenu === row.id ? null : row.id)
          }
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
        >
          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>

        {openMenu === row.id && (
          <DropdownMenu
            buttonRef={buttonRef}
            onClose={() => setOpenMenu(null)}
          >
            <button
              onClick={(e) => {
                openModalForEdit(row);
                setOpenMenu(null);
              }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Edit
            </button>
            <DeleteButton
              style={`text-red-600 hover:text-red-800 px-3 py-2`}
              onDelete={(e) => {
                handleDeleteApproverModule(row.id);
                setOpenMenu(null);
              }}
            />
          </DropdownMenu>
        )}
      </div>
    );
  });

  useEffect(() => {
    setIsClient(true); // Ensures document.body is available
  }, []);

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingStateCode(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((stateCode) => {
    setEditingStateCode(stateCode);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingStateCode(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  // create and update function
  const handleFormSubmit = useCallback(
    async (stateCode) => {
      setLoading(true);
      try {
        if (editingStateCode) {
          // console.log("state code payload", stateCode);
        
          // Convert FormData to object
          // console.log(stateCode instanceof FormData)
          const stateCodeData = stateCode instanceof FormData ? convertFormDataToObject(stateCode) : stateCode;
  
          // console.log("Payload being sent:", { ...editingStateCode, ...stateCodeData });
  
          await updateStateCode({ ...editingStateCode, ...stateCodeData });
          setMessage("State code updated successfully.");
          // setNotification("state code updated successfully.");
          setNotification({
            message: "state code updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create state code", stateCode)
          await addStateCode(stateCode);
          setMessage("state code added successfully.");
          // setNotification("state code added successfully.");
          setNotification({
            message: "State code added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: "Unexpected error during submission:", err,
            type: "error",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [editingStateCode, addStateCode, updateStateCode, closeModal]
  );

  // Memoize state code names
  const stateCodeForTable = useMemo(
    () =>
        stateCodes.map((stateCode) => ({
        ...stateCode,
        name: stateCode?.name || "Unknown State Code",
      })),
    [stateCodes]
  );

  // delete function
  const handleDeleteStateCode = async (id) => {
    try {
      await deleteStateCode(id);
      // setNotification("state cpde deleted successfully");
      setNotification({
        message: "State code deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete state code");
      setNotification({
        message: "Failed to delete state code",
        type: "error",
      });
    }
  };

  // Table columns
  const columns = useMemo(
    () => [
      { label: "Code", field: "code" },
      { label: "Name", field: "name" },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleDeleteApproverModule={handleDeleteStateCode}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleDeleteStateCode,
    ]
  );
  

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-black dark:text-white">State Code Masters</h2>
        <button
          onClick={openModalForAdd}
          className="flex items-center gap-2 font-medium bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
        >
          Add State Code
        </button>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
          {message}
        </div>
      )}

      {/* Candidates Table */}
      <ReusableTable
        data={stateCodeForTable}
        loading={isLoading}
        columns={columns}
        filterable={true}
        excludeFilterColumns={["actions"]}
      />

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingStateCode ? "Edit State Code" : "Add State Code"}
      >
        <StateCodeForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          initialValues={editingStateCode || { code: "", name: "", is_active: "" }}
          errors={formErrors}
        />
      </Modal>
    </div>
  );
});

export default StateCodeMasters;
